import ElementBound from '../core/ElementBound';
import { destoryPanel, getPanels, loadPanel, createLoadPanelOptions } from './PanelManager';
import { parseDomFull, $http } from 'larasnap';
import FormValidation from '../form/FormValidation';
import {
    parseBind,
    snapgrid,
    isJson,
    getObject,
    setObject,
    Message,
    Toast,
    getUrlParam,
} from 'larasnap/support';
import kebabCase from 'lodash-es/kebabCase';
import merge from 'lodash-es/merge';
import first from 'lodash-es/first';
import isObject from 'lodash-es/isObject';

let showTabActivated = false;

export default class extends ElementBound {
    static __larasnap_type = 'panel';

    _parent;
    _snapgrid;
    _snapgridRow;
    _dialog;
    _body;
    _resizeObserver = null;
    _data = {};

    /****************************************************************************************/
    //                                 Constructor / Init
    /****************************************************************************************/

    constructor(props) {
        super();
        this._defaultProps = props;
    }

    init() {
        const props = (this._props = merge(this._defaultProps, this._props));

        if (typeof props.bindTo === 'string') {
            props.bindTo = [props.bindTo];
        } else if (!Array.isArray(props.bindTo)) {
            props.bindTo = [];
        }

        props.bindTo = props.bindTo.map(parseBind);

        this.bindToSnapGrid();
    }

    bindToSnapGrid() {
        this.getBindRelations('SnapGrid').forEach(name => {
            if (this._parent && this._parent.snapgrid(name)) {
                this._snapgrid = this._parent.snapgrid(name);
                return false;
            } else if (snapgrid(name)) {
                this._snapgrid = snapgrid(name);
                return false;
            }
        });
    }

    /****************************************************************************************/
    //                          Create Element / Position Element
    /****************************************************************************************/

    createElement() {
        const props = this._props;
        const name = kebabCase(props.name);

        const html = `
        <div class="panel-wrapper panel-wrapper-${name}" dusk="panel">            
            <div class="panel-dialog" dusk="panel-${name}"></div>
            <div class="panel-loading">
                <table class="loading-layout">
                    <tr>
                        <td><i class="spinner"></i></td>
                        <td><span class="loading-message">${props.loadingMessage}</span></td>
                    </tr>
                </table>      
            </div>
        </div>`;

        const element = $(html);
        element.appendTo('body');
        this.setElement(element);
        element.width(props.width);
        this.positionElement();

        this._dialog = this.find('.panel-dialog');

        if (props.backdropEnabled) {
            this._backdrop = $('<div class="panel-backdrop"></div>');
            $(this._backdrop).appendTo('body');
        }
    }

    positionElement() {
        const numberOfPanels = getPanels({ repositioned: false }).length - 1;

        let top = this._props.defaultOffset.top;
        let left = Math.floor(($(window).width() - this._props.width) / 2);

        top += this._props.spacingOffset.vertical * numberOfPanels;
        left += this._props.spacingOffset.horizontal * numberOfPanels;

        left = left > 0 ? left : 0;

        this._element.css({
            top: `${top}px`,
            left: `${left}px`,
        });
    }

    /****************************************************************************************/
    //                                Load / Set Events
    /****************************************************************************************/

    $http(route, ...params) {
        // If this route is one of the standard CRUD routes then ensure that
        // the primary key of the model is the first param. Generally there
        // should not be any other params.
        if (
            ['save', 'submit', 'destroy', 'restore'].includes(route) &&
            this.datamodel()?.exists()
        ) {
            params = [this.datamodel().getPrimaryKey(), ...params];
        }

        const path = ['panels', this._props.namespace, kebabCase(this._props.name), route].join(
            '/',
        );

        const builder = $http.url('/' + path, ...params);

        const config = this._props.routes.find(x => x.name === route);
        if (!config) {
            throw new Error(`The panel route '${route}' is not defined`);
        }
        builder.method(config.httpMethod);

        if (config.httpMethod.toLowerCase() === 'post' && route !== 'load') {
            const form = this.find('form.panel-form');

            // This ensures that the post data contains the most up to date
            // datamodel data
            this.find('.datamodel').each(function () {
                $(this).getInstance().prepareHttp();
            });

            // By default use the panel's form as the post data. This default post
            // data can be overriden as needed by a specific request
            builder.setData(new FormData(form.get(0)));

            // After the response is received apply form validation errors
            // if response code is 422
            builder.addResponseInterceptor(null, error => {
                if (error?.response?.status === 422) {
                    const validationErrors = Object.entries(error.response.data.errors).map(
                        ([field, message]) => ({ field, message }),
                    );

                    FormValidation(form).apply(validationErrors);
                }
                return Promise.reject(error);
            });
        }

        return builder;
    }

    load(...params) {
        let queryString = {
            parent_id: this.getParentKey(),
        };
        let postData = {};

        if (isObject(params[0])) {
            const options = params[0];
            params = options.params;
            queryString = {
                ...queryString,
                ...options.queryString,
            };
            if (options.postData) {
                postData = options.postData;
            }
        }

        this._props.params = params;
        this.createElement();
        this.focus();

        this.$http('load', ...params)
            .addParams(queryString)
            .addData(postData)
            .send()
            .then(({ data: html }) => this.loadComplete(html))
            .catch(error => {
                console.error('Error, closing panel', error);
                this.close();
            });
    }

    loadComplete(html) {
        if (!html) {
            this.close();
            return;
        }

        this._dialog.html(html);
        this.find('.panel-loading').hide();
        this._body = this.find('.panel-body');
        this.focus();

        if (!this._props.reloadEnabled) {
            this.find('.panel-control-reload').hide();
        }

        if (isJson(this.find('input[name=panel-data]').val())) {
            this._data = JSON.parse(this.find('input[name=panel-data]').val());
        }

        parseDomFull();

        this.setEvents();

        if (this._props.draggable) {
            this.enableDrag();
        }

        if (this._props.height === 'max' || this._props.height === 'constrained') {
            setTimeout(this.enableDynamicHeight.bind(this));
        }

        this.initShareControls();

        if (!this.footerShouldBeVisible()) {
            this.hideFooter();
        }

        const showTab = getUrlParam('tab');
        if (showTab && !showTabActivated) {
            this.find(`ul.nav-tabs a[href="#${showTab}"]`).click();
            showTabActivated = true;
        }

        this.runHandler('load-complete');
    }

    footerShouldBeVisible() {
        return (
            this.find('.panel-share-button').is(':visible') ||
            this.find('.panel-options-menu').length ||
            this.find('.panel-footer-buttons-left').html().trim()
        );
    }

    enableDynamicHeight() {
        if ('ResizeObserver' in window) {
            this._resizeObserver = new ResizeObserver(this.checkDialogHeight.bind(this));
            this._resizeObserver.observe(this._body.get(0));
        } else {
            this.checkDialogHeight();
        }

        const height =
            this.find('.panel-header').outerHeight() + this.find('.panel-footer').outerHeight();

        this._body.css('max-height', `calc(100% - ${height}px)`);
    }

    checkDialogHeight() {
        if (this._props.height === 'max') {
            this.maximizeHeight(true);
        }
        if (this._props.height === 'constrained') {
            this.maximizeHeight(false);
        }
    }

    getBodyHeight() {
        const height =
            this.find('.panel-dialog').height() -
            this.find('.panel-header').outerHeight() -
            this.find('.panel-footer').outerHeight();

        return height;
    }

    recheckHeight() {
        if (this._props.height === 'constrained') {
            this.maximizeHeight(false);
        }
    }

    maximizeHeight(force = false) {
        const availableHeight = $(window).height() - this._dialog.offset().top;

        if (force || this._dialog.height() > availableHeight) {
            const bottomSpacing = 20;
            this._dialog.height(availableHeight - bottomSpacing);
            this.dialogHeightChanged();
        }
    }

    dialogHeightChanged() {}

    setEvents() {
        this.find('.panel-control-reload').click(this.reload.bind(this));
        this.find('.panel-control-close').click(this.close.bind(this));

        this._dialog.click(e => {
            if (!$(e.target).hasClass('loads-panel')) this.focus();
        });
    }

    initShareControls() {
        // Load the full url override in props
        this._props.shareUrlOverride = this.find('input[name=panel-share-url]').val();

        // Check for a share route override and override the existing shareRoute if found
        const shareRoute = this.find('input[name=panel-share-route]').val();
        if (shareRoute) {
            this._props.shareRoute = shareRoute;
        }

        if (this.shouldShowShareControls()) {
            this.find('.panel-share-button').show().click(this.showShareInfo.bind(this));
        }
    }

    shouldShowShareControls() {
        return (
            this._props.shareUrlOverride ||
            (this._props.shareRoute && this.hasDatamodel() && this.datamodel().exists())
        );
    }

    async getShareUrl() {
        if (this._props.shareUrlOverride) {
            return this._props.shareUrlOverride;
        } else {
            const shareId = this.datamodel().getPrimaryKey();
            const { shareRoute, name } = this._props;

            return $http.route('get_panel_route', shareRoute, name, shareId).get();
        }
    }

    async showShareInfo() {
        const { data: url } = await this.getShareUrl();

        Message.info({
            title: 'Share URL',
            input: 'textarea',
            inputValue: url,
            showCloseButton: true,
            confirmButtonText: 'Copy To Clipboard',
        }).then(({ isConfirmed, value }) => {
            if (isConfirmed) {
                navigator.clipboard.writeText(value);
                Toast.success('URL copied to clipboard');
            }
        });
    }

    /****************************************************************************************/
    //                                  Save / Delete
    /****************************************************************************************/

    saveAsNew(button) {
        this.datamodel().setPrimaryKey(null);
        button.dispatch();
    }

    saveComplete(context, response) {
        if (response.success === false) return;

        if (!context.ctrlKeyWasHeld()) {
            this.close();
        }

        this.reloadSnapGrid();
        this.runHandler('save-complete');
    }

    deleteComplete(context, response) {
        if (response.success === false) return;

        this.close();
        this.reloadSnapGrid();
        this.runHandler('delete-complete');
    }

    restoreComplete(context, response) {
        if (response.success === false) return;

        this.reload();
        this.reloadSnapGrid();
        this.runHandler('restore-complete');
    }

    reloadSnapGrid() {
        this._snapgrid?.reload({ hidden: true });
    }

    /****************************************************************************************/
    //                                    Behaviours
    /****************************************************************************************/

    focus() {
        this.setZIndex(this.getHighestZIndex() + 2);
    }

    close() {
        this.runHandler('close');
        this._element.remove();

        if (this._backdrop) {
            this._backdrop.remove();
        }

        destoryPanel(this.getId());

        const id = this._props.params[0];
        if (this._snapgrid && id) {
            this._snapgrid.unbind('Panel', this._props.name, id);
        }

        this._resizeObserver?.unobserve(this._body.get(0));
    }

    shake() {
        this._element.effect('shake', { distance: 4 });
    }

    /****************************************************************************************/
    //                                    Reload
    /****************************************************************************************/

    reload() {
        this._dialog.hide();
        this.find('.panel-loading').show();
        return this.$http('load', ...this._props.params)
            .send()
            .then(({ data: html }) => {
                this.loadComplete(html);
                this._dialog.show();
                this.reloadComplete();
            });
    }

    reloadComplete() {}

    /****************************************************************************************/
    //                                Binds / SnapGrid
    /****************************************************************************************/

    getBindRelations(type) {
        return this._props.bindTo.filter(x => x.type === type).map(x => x.name);
    }

    getBindRelation(type) {
        return first(this.getBindRelations(type));
    }

    getBoundSnapGrid() {
        return this._snapgrid;
    }

    hasBoundSnapGrid() {
        return !!this._snapgrid;
    }

    setSnapGridRow(row) {
        this._snapgridRow = row;
    }

    getSnapGridRow() {
        return this._snapgridRow;
    }

    /****************************************************************************************/
    //                                   Utilities
    /****************************************************************************************/

    getParentKey() {
        if (this.hasParent() && this._parent.hasDatamodel()) {
            return this._parent.datamodel().getPrimaryKey();
        }
    }

    setZIndex(val) {
        this._element.css('z-index', this._props.baseZIndex + val);
        if (this._backdrop) {
            this._backdrop.css('z-index', this._props.baseZIndex + (val - 1));
        }

        this._props.zIndex = val;
    }

    getHighestZIndex() {
        let highestZIndex = 0;
        $('.panel-wrapper').each(function () {
            const panel = $(this).getInstance();
            if (panel.getProps('zIndex') && Number(panel.getProps('zIndex')) > highestZIndex) {
                highestZIndex = Number(panel.getProps('zIndex'));
            }
        });

        return highestZIndex;
    }

    enableDrag() {
        this._element.draggable({
            handle: '.panel-header',
            start: () => {
                this._props.repositioned = true;
            },
        });
    }

    loadPanel(...args) {
        const options = createLoadPanelOptions(...args);
        options.parent = this;
        return loadPanel(options);
    }

    setWidth(width = null) {
        if (width === null) {
            width = this.width;
        }

        if (width !== null) {
            this._element.find('.modal-dialog').width(width);
        }
    }

    setHeaderText(val) {
        this.find('.panel-header-text').html(val);
    }

    setBody(val) {
        this._body.html(val);
    }

    setParent(val) {
        this._parent = val;
    }

    getParent() {
        return this._parent;
    }

    hasParent() {
        return !!this._parent;
    }

    showError(msg) {
        this.find('.panel-error-message')
            .html(`<i class="fa-solid fa-exclamation-circle pr-1"></i> ${msg}`)
            .show(50);
    }

    hideError() {
        this._element.find('.panel-error-message').hide(50);
    }

    showFooter() {
        this.find('.panel-footer').show();
    }

    hideFooter() {
        this.find('.panel-footer').hide();
    }

    getData(key = null) {
        return getObject(this._data, key);
    }

    setData(key, value = null) {
        return setObject(this._data, key, value);
    }
}
